import { graphql } from "gatsby";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CtaComponent from "../components/CtaComponent";
import Footer from "../components/Footer";
import Header from "../components/Header";
import Landing from "../components/Landing";
import OrderNowBanner from "../components/OrderNowBanner";
import OurMenu from "../components/OurMenu";
import OurStory from "../components/OurStory";
import PreFooter from "../components/PreFooter";
import Separator from "../components/Separator";
import "../styles/legal.scss";
import CountriesPopup from "../components/CountriesPopup";
import useOnScreen from "../utils/useOnScreen";
import {
  CookieDialogContainer,
  CookieDialogContainerRef,
} from "@hfc/cookie-banner";
import Language from "../components/Language";

const Home = ({ data, location }) => {
  const { site = {}, logo = {}, altLogo = {}, mobileLogo = {} } = data || {},
    { siteMetadata = {} } = site;
  const { t, i18n } = useTranslation();
  let activeElementInitial = location?.state?.activeElement ?? null;
  const [activeElement, setActiveElement] = useState(activeElementInitial);
  let ourStory = useRef(null),
    ourMenu = useRef(null),
    followUs = useRef(null);
  let [isOurStoryActive, ourStoryIntersectionRatio] = useOnScreen(
    ourStory,
    () => setActiveElement("our-story")
  );
  let [isOurMenuActive, ourMenuIntersectionRatio] = useOnScreen(ourMenu, () =>
    setActiveElement("our-menu")
  );
  let [isFollowUsActive, followUsIntersectionRatio] = useOnScreen(
    followUs,
    () => setActiveElement("follow")
  );

  const elemRefs = {
    "our-story": ourStory,
    "our-menu": ourMenu,
    follow: followUs,
  };
  const handleLinkClicks = (selector) => {
    if (typeof window !== "undefined") {
      let element = elemRefs[selector]?.current;
      if (selector === "landing") {
        window.scrollTo(0, 0);
      } else if (element) {
        if (selector === "landing") {
          window.scrollTo(0, 0);
        } else {
          element.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "start",
          });
        }
      }
    }
  };
  useEffect(() => {
    if (activeElementInitial) {
      handleLinkClicks(activeElementInitial);
    }
  }, []);
  useEffect(() => {
    if (!isFollowUsActive && !isOurMenuActive && !isOurStoryActive) {
      setActiveElement(null);
    }
    if (isOurMenuActive && isFollowUsActive) {
      setActiveElement(
        ourMenuIntersectionRatio > followUsIntersectionRatio
          ? "our-menu"
          : "follow"
      );
    }
  }, [
    isOurStoryActive,
    isOurMenuActive,
    isFollowUsActive,
    ourStoryIntersectionRatio,
    ourMenuIntersectionRatio,
    followUsIntersectionRatio,
  ]);

  const { childImageSharp = {} } = logo,
    { fixed = {} } = childImageSharp,
    { childImageSharp: altLogoChild = {} } = altLogo,
    { fixed: altLogoFixed = {} } = altLogoChild,
    { childImageSharp: mobileLogoChild } = mobileLogo,
    { fixed: mobileLogoFixed } = mobileLogoChild;
  return (
    <div className="layout-container">
      <Language />
      <Header
        siteMetadata={siteMetadata}
        lang={i18n.language}
        logo={fixed}
        altLogo={altLogoFixed}
        mobileLogo={mobileLogoFixed}
        activeElement={activeElement}
        onLinkClick={handleLinkClicks}
        elemRefs={elemRefs}
      />
      <Landing lang={i18n.language} />
      <OurStory ref={ourStory} />
      <OrderNowBanner />
      <OurMenu ref={ourMenu} />
      <Separator ref={followUs} />
      <CtaComponent />
      <PreFooter />
      <Footer />
      <CountriesPopup />
      <CookieDialogContainer
        privacyLink={`${location && location.href}privacy`}
        ref={CookieDialogContainerRef.getInstance().cookieRef}
        onCookieUpdated={(cookieLevel) => {
          window.dataLayer.push({ event: "cookie-accepted-noahsgreenkitchen" });
        }}
        theme={{
          colorPrimary: "#009341",
          colorSecondary: "#F9EEDB",
          colorTint: "white",
          colorDark: "black",
          fontFamily: "Poppins",
        }}
        buttonSwtich={true}
      />
    </div>
  );
};

export const query = graphql`
  query {
    site {
      siteMetadata {
        title
        description
        siteUrl
      }
    }
    logo: file(relativePath: { eq: "logo.png" }) {
      childImageSharp {
        fixed(width: 100, height: 64) {
          ...GatsbyImageSharpFixed
        }
      }
    }
    mobileLogo: file(relativePath: { eq: "logo.png" }) {
      childImageSharp {
        fixed(width: 82, height: 56) {
          ...GatsbyImageSharpFixed
        }
      }
    }
    altLogo: file(relativePath: { eq: "alt_logo.png" }) {
      childImageSharp {
        fixed(width: 50, height: 50) {
          ...GatsbyImageSharpFixed
        }
      }
    }
  }
`;

export default Home;
